import React, {useState, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import {
    getMassUploadFileDetails,
    deleteMassUploadParcels,
    deleteMassUploadFile,
    createParcelsFromFile,
    updateMassUploadParcel
} from '../../services/massUploadApi';
import {toast} from 'react-toastify';
import GridHeader from '../../components/GridHeader';
import MassUploadTableGrid from './components/MassUploadTableGrid';
import PositionsModal from './components/PositionsModal';
import {Button} from 'semantic-ui-react';
import {getRoles} from '../../services/userRights';
import './massUploadEditPage.css';

/**
 * Страница редактирования массовой загрузки (новая версия с GridPage стилями и модалками)
 */
const MassUploadEditPage = ({match, history}) => {
    const fileId = parseInt(match.params.fileId);
    const [fileDetails, setFileDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedRows, setSelectedRows] = useState([]);
    const [positionsModal, setPositionsModal] = useState({ open: false, parcel: null });
    const [isCreatingParcels, setIsCreatingParcels] = useState(false);
    const mountedRef = useRef(true);
    const tableScrollRef = useRef(null);
    const tableContentRef = useRef(null);
    const scrollPositionRef = useRef({ left: 0, top: 0 });
    const shouldRestoreScrollRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        loadFileDetails();
        
        const interval = setInterval(() => {
            if (fileDetails && fileDetails.fileInfo && fileDetails.fileInfo.status === 'Processing') {
                loadFileDetails();
            }
        }, 3000);

        return () => { 
            mountedRef.current = false; 
            clearInterval(interval); 
        };
    }, [fileId, fileDetails && fileDetails.fileInfo && fileDetails.fileInfo.status]);

    // Восстанавливаем позицию скролла СИНХРОННО (до отрисовки)
    useLayoutEffect(() => {
        if (shouldRestoreScrollRef.current && tableScrollRef.current && tableContentRef.current) {
            // Используем тройной requestAnimationFrame для надежного восстановления после полной отрисовки DOM
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (tableScrollRef.current) {
                            // Восстанавливаем вертикальный скролл
                            tableScrollRef.current.scrollTop = scrollPositionRef.current.top;
                            
                            // Восстанавливаем горизонтальный скролл
                            tableScrollRef.current.scrollLeft = scrollPositionRef.current.left;
                        }
                        shouldRestoreScrollRef.current = false;
                    });
                });
            });
        } else if (tableScrollRef.current && scrollPositionRef.current.left > 0) {
            // Если shouldRestoreScrollRef не установлен, но есть позиция - применяем её
            tableScrollRef.current.scrollLeft = scrollPositionRef.current.left;
        }
    }, [fileDetails, gridRows]);

    const loadFileDetails = (showLoader = true) => {
        if (showLoader) {
            setLoading(true);
        }
        getMassUploadFileDetails(fileId)
            .then(data => {
                if (!mountedRef.current) return;
                setFileDetails(data);
                if (showLoader) {
                    setLoading(false);
                }
            })
            .catch(err => {
                if (!mountedRef.current) return;
                console.error(err);
                toast.error('Ошибка при загрузке данных файла');
                if (showLoader) {
                    setLoading(false);
                }
            });
    };

    const handleRowSelect = (parcelId, checked) => {
        if (checked) {
            setSelectedRows(prev => [...prev, parcelId]);
        } else {
            setSelectedRows(prev => prev.filter(id => id !== parcelId));
        }
    };

    const handleSelectAll = (checked) => {
        if (!fileDetails) return;
        if (checked) {
            setSelectedRows(fileDetails.parcels.map(p => p.id));
        } else {
            setSelectedRows([]);
        }
    };

    const handleDeleteSelected = () => {
        if (!fileDetails || !fileDetails.fileInfo || !fileDetails.fileInfo.canDelete) {
            toast.warning('После запуска создания накладных удаление строк недоступно');
            return;
        }

        if (selectedRows.length === 0) {
            toast.warning('Выберите строки для удаления');
            return;
        }
        if (confirm(`Удалить ${selectedRows.length} выбранных строк?`)) {
            deleteMassUploadParcels(fileId, selectedRows)
                .then(() => {
                    toast.success('Строки удалены');
                    setSelectedRows([]);
                    loadFileDetails();
                })
                .catch(err => {
                    console.error(err);
                    toast.error('Ошибка при удалении строк');
                });
        }
    };

    const handleDeleteAll = () => {
        if (!fileDetails || !fileDetails.fileInfo || !fileDetails.fileInfo.canDelete) {
            toast.warning('После запуска создания накладных удаление списка недоступно');
            return;
        }

        if (confirm('Вы уверены, что хотите удалить весь список?')) {
            deleteMassUploadFile(fileId)
                .then(() => {
                    toast.success('Список удален');
                    history.push('/massupload');
                })
                .catch(err => {
                    console.error(err);
                    toast.error('Ошибка при удалении списка');
                });
        }
    };

    const handleCreateParcels = () => {
        if (!fileDetails) {
            return;
        }

        // Проверяем реальные ошибки из parcels, а не только fileInfo.errorRows
        // (fileInfo.errorRows может быть устаревшим после редактирования строк)
        const hasErrors = fileDetails.parcels && fileDetails.parcels.some(p => !p.isValid);
        if (hasErrors) {
            toast.error('В файле есть строки с ошибками. Исправьте ошибки или удалите эти строки.');
            return;
        }

        if (fileDetails.parcels.length === 0) {
            toast.error('Нет отправлений для создания');
            return;
        }

        if (isCreatingParcels) {
            return;
        }

        setIsCreatingParcels(true);
        createParcelsFromFile(fileId)
            .then(result => {
                toast.success('Отправления переведены в статус "Создано"');
                
                const filtersObj = { 
                    massUploadFileId: [fileId.toString()]
                };
                const filters = encodeURIComponent(JSON.stringify(filtersObj));
                window.location.assign(`/parcels/list/new?filters=${filters}`);
            })
            .catch(err => {
                console.error('Error creating parcels:', err);
                
                // Обрабатываем различные форматы ошибок
                let errorMessage = 'Ошибка при создании отправлений';
                
                if (err && typeof err === 'object') {
                    // Если ошибка содержит поле Error (из ErrorResult) - это основной формат
                    if (err.Error) {
                        errorMessage = err.Error;
                    }
                    // Если ошибка содержит поле error (в нижнем регистре)
                    else if (err.error) {
                        errorMessage = err.error;
                    }
                    // Если ошибка содержит поле message
                    else if (err.message) {
                        errorMessage = err.message;
                    }
                    // Если ошибка в response.data (для axios)
                    else if (err.response && err.response.data) {
                        if (err.response.data.Error) {
                            errorMessage = err.response.data.Error;
                        } else if (err.response.data.error) {
                            errorMessage = err.response.data.error;
                        } else if (err.response.data.message) {
                            errorMessage = err.response.data.message;
                        }
                    }
                } else if (typeof err === 'string') {
                    errorMessage = err;
                }
                
                // Логируем финальное сообщение для отладки
                console.log('Final error message:', errorMessage);
                
                // Показываем ошибку (toast может обрезать длинные сообщения, но показываем что есть)
                toast.error(errorMessage, {
                    autoClose: 10000, // Показываем дольше для длинных сообщений
                    style: { whiteSpace: 'pre-wrap' } // Разрешаем перенос строк
                });
                setIsCreatingParcels(false);
            });
    };

    const handleBeforeEditOpen = () => {
        // Фиксируем текущую позицию скролла перед открытием модалки
        if (tableScrollRef.current) {
            scrollPositionRef.current = {
                left: tableScrollRef.current.scrollLeft,
                top: tableScrollRef.current.scrollTop
            };
        }
    };

    const handleSaveEdit = (parcelId, fieldName, value) => {
        // Отмечаем что нужно восстановить скролл
        shouldRestoreScrollRef.current = true;
        
        // Перезагружаем данные для валидации БЕЗ лоадера - скролл восстановится через useLayoutEffect
        loadFileDetails(false);
    };

    const openPositions = (rowId) => {
        const parcel = fileDetails.parcels.find(p => p.id === rowId);
        if (parcel) {
            // Фиксируем текущую позицию скролла перед открытием модалки
            if (tableScrollRef.current) {
                scrollPositionRef.current = {
                    left: tableScrollRef.current.scrollLeft,
                    top: tableScrollRef.current.scrollTop
                };
            }
            setPositionsModal({ open: true, parcel });
        }
    };

    const savePositions = (arrayKey, positions) => {
        const parcel = positionsModal.parcel;
        if (!parcel) return Promise.reject(new Error('Отправление не найдено'));
        
        // Создаем updatedData с сохранением всех данных из parcel.data
        const updatedData = { 
            ...parcel.data, 
            rowNumber: parcel.rowNumber,
            // ВАЖНО: Используем стандартное имя 'positions' (camelCase) для согласованности с бэком
            // Это гарантирует правильную десериализацию на бэке
            positions: positions 
        };
        
        return updateMassUploadParcel(parcel.id, updatedData)
            .then(() => {
                toast.success('Позиции обновлены');
                setPositionsModal({ open: false, parcel: null });
                
                // Отмечаем что нужно восстановить скролл
                shouldRestoreScrollRef.current = true;
                
                // Перезагружаем данные для валидации БЕЗ лоадера - скролл восстановится через useLayoutEffect
                loadFileDetails(false);
            })
            .catch(err => {
                console.error('Ошибка при обновлении позиций:', err);
                toast.error('Ошибка при обновлении позиций');
                setPositionsModal({ open: false, parcel: null });
                throw err; // Пробрасываем ошибку дальше для обработки в модалке
            });
    };

    // Подготовка колонок для TableGrid
    const columns = useMemo(() => {
        if (!fileDetails || !fileDetails.parcels || fileDetails.parcels.length === 0) return [];

        const userRoles = getRoles() || [];
        const isClientOnly = (userRoles.includes('Author') || userRoles.includes('Demo')) && 
                            !userRoles.includes('Coordinator') && 
                            !userRoles.includes('Admin') && 
                            !userRoles.includes('GlobalAdmin');
        
        // Скрываем поля для клиентов и администраторов клиента
        const shouldHideClientFields = isClientOnly;

        const displayNameMap = {
            sequentialNumber: '№ ПП',
            parcelType: 'Тип',
            cargoDescription: 'Описание содержимого',
            declaredCargoValue: 'Ваше отправление застраховано на',
            userCompanyName: 'Юр. лицо клиента',
            senderCompany: 'Компания отправителя',
            senderAddress: 'Адрес отправления',
            templateSenderAddress: 'Вы указали в шаблоне',
            senderContactPerson: 'Контакт отправителя',
            senderPhone: 'Телефон отправителя',
            senderExt1: 'Доб.№1 отпр.',
            senderAdditionalPhone: 'Доп.тел. отпр.',
            senderAdditionalPhone2: 'Доб.№2 отпр.',
            shipmentAddressComment: 'Комментарий адрес отпр.',
            recipientCompany: 'Компания получателя',
            recipientAddress: 'Адрес доставки',
            templateRecipientAddress: 'Вы указали в шаблоне',
            recipientContactPerson: 'Контакт получателя',
            recipientPhone: 'Телефон получателя',
            recipientExt1: 'Доб.№1 получ.',
            recipientAdditionalPhone: 'Доп.тел. получ.',
            recipientAdditionalPhone2: 'Доб.№2 получ.',
            deliveryAddressComment: 'Комментарий адрес дост.',
            approximateVolume: 'Объем всех мест',
            approximateWeight: 'Расчетный вес всех мест',
            packagesCount: 'Кол-во мест',
            departureDate: 'Дата отгрузки',
            costCenter: 'Кост центр',
            initiator: 'Инициатор',
            serviceType: 'Вид услуги',
            additionalServices: 'Дополнительные услуги',
            serviceComment: 'Комментарий к услуге',
            paymentType: 'Вид оплаты',
            payer: 'Плательщик',
            orderNumber: '№ заказа',
            invoiceNumber: '№ счета',
            clientCode: 'Код клиента'
        };

        const preferredOrder = [
            'sequentialNumber',
            'senderCompany',
            'senderContactPerson',
            'senderPhone',
            'senderExt1',
            'senderAdditionalPhone',
            'senderAdditionalPhone2',
            'senderAddress',
            'templateSenderAddress',
            'shipmentAddressComment',
            'recipientCompany',
            'recipientContactPerson',
            'recipientPhone',
            'recipientExt1',
            'recipientAdditionalPhone',
            'recipientAdditionalPhone2',
            'recipientAddress',
            'templateRecipientAddress',
            'deliveryAddressComment',
            'approximateWeight',
            'packagesCount',
            'approximateVolume',
            'cargoDescription',
            'declaredCargoValue',
            'departureDate',
            'parcelType',
            'userCompanyName',
            'initiator',
            'costCenter'
        ];

        const exclude = new Set([
            'rowNumber', 
            'shipmentDate',
            'serviceType',
            'additionalServices',
            'serviceComment',
            'paymentType',
            'payer',
            'orderNumber',
            'invoiceNumber',
            'clientCode'
            // Оставляем видимыми: ext номера, доп. телефоны, комментарии к адресам
        ]);

        const allKeys = new Set();
        fileDetails.parcels.forEach(p => {
            const data = p.data || {};
            Object.keys(data).forEach(k => {
                if (exclude.has(k)) return;
                const v = data[k];
                if (v && typeof v === 'object') return;
                allKeys.add(k);
            });
        });

        const keys = Array.from(allKeys);
        keys.sort((a,b) => {
            const ia = preferredOrder.indexOf(a);
            const ib = preferredOrder.indexOf(b);
            if (ia === -1 && ib === -1) return a.localeCompare(b);
            if (ia === -1) return 1;
            if (ib === -1) return -1;
            return ia - ib;
        });

        // Фильтруем поля для клиентов и администраторов клиента
        const filteredKeys = shouldHideClientFields 
            ? keys.filter(key => key !== 'userCompanyName')
            : keys;

        return filteredKeys.map(key => ({
            fieldName: key,
            title: displayNameMap[key] || key,
            type: key,
            filterType: 'text',
            disableSort: false,
            canEdit: !['templateSenderAddress', 'templateRecipientAddress'].includes(key),
            highlighted: ['templateSenderAddress', 'templateRecipientAddress'].includes(key),
            minWidth: key === 'sequentialNumber' ? '60px' :
                     key.includes('Address') ? '250px' : 
                     key === 'cargoDescription' ? '250px' :
                     key === 'declaredCargoValue' ? '280px' :
                     key === 'approximateWeight' ? '200px' :
                     key === 'recipientContactPerson' ? '180px' :
                     key === 'senderContactPerson' ? '180px' :
                     key.includes('Phone') ? '160px' : 
                     key.includes('Company') ? '200px' :
                     '150px',
            maxWidth: key.includes('Address') ? '350px' : 
                     key === 'cargoDescription' ? '350px' :
                     key === 'declaredCargoValue' ? '400px' :
                     key === 'approximateWeight' ? '300px' :
                     key === 'recipientContactPerson' ? '300px' :
                     key === 'senderContactPerson' ? '300px' :
                     key.includes('Company') ? '250px' :
                     '300px'
        }));
    }, [fileDetails]);

    // Подготовка данных для TableGrid
    const gridRows = useMemo(() => {
        if (!fileDetails || !fileDetails.parcels) return [];
        
        return fileDetails.parcels.map(parcel => ({
            id: parcel.id,
            rowNumber: parcel.rowNumber,
            isValid: parcel.isValid,
            validationErrors: parcel.validationErrors,
            invalidFields: parcel.invalidFields || [],
            ...parcel.data,
            // Для TableGrid
            _parcel: parcel // Сохраняем полный объект для модалки
        }));
    }, [fileDetails]);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleString('ru-RU');
    };

    // Функция для обрезки длинного названия файла
    const truncateFileName = (fileName, maxLength = 50) => {
        if (!fileName || fileName.length <= maxLength) {
            return fileName;
        }
        return fileName.substring(0, maxLength) + '...';
    };

    // Функция для определения, можно ли редактировать ячейку
    const canEditByRow = (column, row) => {
        return true; // Можно редактировать все ячейки
    };

    if (loading) {
        return (
            <div className="content-wrapper">
                <section className="content">
                    <div className="box">
                        <div className="box-body">
                            <div className="text-center" style={{padding: '50px'}}>
                                <i className="fa fa-spinner fa-spin fa-3x text-primary"></i>
                                <p style={{marginTop: '20px'}}>Загрузка данных...</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    if (!fileDetails) {
        return (
            <div className="content-wrapper">
                <section className="content">
                    <div className="box box-danger">
                        <div className="box-body">
                            <div className="alert alert-danger">
                                <i className="fa fa-exclamation-triangle"></i> Файл не найден
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // Проверяем реальные ошибки из parcels, а не только fileInfo.errorRows
    const hasErrors = fileDetails.parcels && fileDetails.parcels.some(p => !p.isValid);
    const totalErrors = fileDetails.parcels ? fileDetails.parcels.filter(p => !p.isValid).length : fileDetails.fileInfo.errorRows;
    const canCreateParcels = !hasErrors && fileDetails.parcels.length > 0;
    const isProcessing = fileDetails.fileInfo.status === 'Processing';
    const isParcelsAlreadyCreated = !!fileDetails.fileInfo.parcelsCreatedDate;
    const canDeleteDraftRows = !!fileDetails.fileInfo.canDelete;

    const handleTableScroll = (e) => {
        scrollPositionRef.current.left = e.target.scrollLeft;
        scrollPositionRef.current.top = e.target.scrollTop;
    };

    return (
        <div className='new-styles new-styles__p-0' style={{overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100vh'}}>
            <div className='grid__header'>
                <GridHeader
                    name={`Массовая загрузка накладных / ${truncateFileName(fileDetails.fileInfo.originalFileName)}`}
                    title={`Массовая загрузка накладных / ${fileDetails.fileInfo.originalFileName}`}
                    btn={
                        <div style={{display: 'flex', gap: '8px'}}>
                            {canDeleteDraftRows && (
                                <Button
                                    size="small"
                                    onClick={handleDeleteSelected} 
                                    disabled={selectedRows.length === 0}
                                    title="Удалить выбранные строки"
                                >
                                    <i className="fa fa-trash"></i> Удалить выбранные
                                </Button>
                            )}
                            {canDeleteDraftRows && (
                                <Button
                                    size="small"
                                    onClick={handleDeleteAll}
                                    title="Удалить весь файл"
                                >
                                    <i className="fa fa-trash"></i> Удалить все
                                </Button>
                            )}
                            {!isParcelsAlreadyCreated && (
                                <Button
                                    className='btn_light-blue btn_icon_plus'
                                    onClick={handleCreateParcels} 
                                    disabled={!canCreateParcels || isCreatingParcels}
                                    loading={isCreatingParcels}
                                    title={!canCreateParcels ? 'Исправьте все ошибки перед созданием отправлений' : isCreatingParcels ? 'Создание отправлений...' : 'Создать отправления'}
                                >
                                    {isCreatingParcels ? 'Создание...' : 'Создать отправления'}
                                </Button>
                            )}
                        </div>
                    }
                />
            </div>

            {/* Информация об ошибках */}
            {!isProcessing && (
                <div style={{padding: '15px 20px', backgroundColor: '#fff', borderBottom: '1px solid #ddd'}}>
                    {totalErrors > 0 ? (
                        <span className="text-warning" style={{fontSize: '14px'}}>
                            <i className="fa fa-exclamation-triangle"></i>
                            {' '}Проектов накладных с ошибками <strong className="text-danger">{totalErrors} шт.</strong>
                        </span>
                    ) : (
                        <span className="text-success" style={{fontSize: '14px'}}>
                            <i className="fa fa-check-circle"></i>
                            {' '}Все записи корректны
                        </span>
                    )}
                </div>
            )}

            {/* Таблица с данными */}
            {!isProcessing && (
                <div style={{
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0
                }}>
                    <div
                        ref={tableScrollRef}
                        onScroll={handleTableScroll}
                        style={{
                            overflowX: 'auto',
                            overflowY: 'auto',
                            backgroundColor: '#fff',
                            flex: 1,
                            minHeight: 0
                        }}
                    >
                        <div ref={tableContentRef} style={{minWidth: '100%', width: 'max-content'}}>
                            <MassUploadTableGrid
                                columns={columns}
                                rows={gridRows}
                                selectedIds={selectedRows}
                                onSelectChange={handleRowSelect}
                                onSelectAll={handleSelectAll}
                                canDeleteRows={canDeleteDraftRows}
                                onEditPositions={openPositions}
                                reload={loadFileDetails}
                                loading={loading}
                                onBeforeEditOpen={handleBeforeEditOpen}
                                onSaveEdit={handleSaveEdit}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Модальное окно позиций */}
            <PositionsModal
                open={positionsModal.open}
                parcel={positionsModal.parcel}
                onClose={() => setPositionsModal({ open: false, parcel: null })}
                onSave={savePositions}
            />
        </div>
    );
};

export default MassUploadEditPage;




// WEBPACK FOOTER //
// ./App/pages/massUpload/massUploadEditPage.jsx